import { useEffect } from 'react';

const SITE = 'https://mondainestyle.com';

/**
 * Lightweight per-page SEO hook.
 * Sets <title>, <meta name="description">, <link rel="canonical"> and the
 * og:title / og:description / og:url / twitter:title / twitter:description tags.
 *
 * Optionally injects per-page Schema.org JSON-LD via the `jsonLd` array.
 * Each entry is injected with a stable id (`page-jsonld-<index>`) so it can
 * be replaced/removed when navigating between routes.
 *
 * Usage:
 *   useSEO({
 *     title: 'About | Mondaine Style',
 *     description: '...',
 *     path: '/about',
 *     jsonLd: [{ '@context': 'https://schema.org', '@type': 'Person', ... }],
 *   });
 */
export default function useSEO({ title, description, path = '/', jsonLd }) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [key, val] = selector.replace(/[\[\]"]/g, '').split('=');
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE}${path}`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE}${path}`);

    // Per-page JSON-LD — replace any from a previous page
    document.querySelectorAll('script[data-page-jsonld]').forEach((n) => n.remove());
    if (Array.isArray(jsonLd) && jsonLd.length) {
      jsonLd.forEach((entry, i) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-page-jsonld', String(i));
        s.textContent = JSON.stringify(entry);
        document.head.appendChild(s);
      });
    }

    return () => {
      document.querySelectorAll('script[data-page-jsonld]').forEach((n) => n.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, jsonLdKey]);
}

// Helpers --------------------------------------------------------------------

export const orgRef = { '@type': 'Organization', name: 'Mondaine Style', url: SITE };

export function serviceJsonLd({ name, description, slug, price }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType: name,
    provider: orgRef,
    areaServed: ['India', 'Singapore', 'Dubai', 'China'],
    url: `${SITE}${slug}`,
    ...(price && {
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price,
        availability: 'https://schema.org/InStock',
        url: `${SITE}${slug}`,
      },
    }),
  };
}

export function faqJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function breadcrumbJsonLd(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `${SITE}${c.path}`,
    })),
  };
}
